import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type MouseEvent,
} from "react";
import type { Entity } from "@kkindex/shared";
import { calendarDay, type CalendarBar } from "./calendarLayout";
import { moveRelative } from "./taskLayout";
import type { Execute } from "./Editor";

type Target = { id: string; segment: string; date: string; after: boolean };
export function useCalendarDrag(tasks: Entity[], execute: Execute) {
  const cleanup = useRef<(() => void) | null>(null);
  const suppress = useRef<{ id: string; until: number } | null>(null);
  const [drag, setDrag] = useState<{
    id: string;
    dy: number;
    target: Target | null;
  } | null>(null);
  useEffect(() => () => cleanup.current?.(), []);

  function start(
    event: ReactPointerEvent<HTMLElement>,
    bar: CalendarBar,
    date: string,
    enabled: boolean,
  ) {
    suppress.current = null;
    if (
      !enabled ||
      bar.event ||
      event.button !== 0 ||
      event.pointerType !== "mouse" ||
      (event.target as Element).closest(
        "button,a,textarea,select,input:not([data-inline-title][readonly]),[role=separator],[data-no-drag]",
      )
    )
      return;
    const element = event.currentTarget;
    const scope = element.closest<HTMLElement>("[data-calendar-sort-scope]");
    if (!scope) return;
    event.preventDefault();
    event.stopPropagation();
    element.focus({ preventScroll: true });
    cleanup.current?.();
    const origin = {
      x: event.clientX,
      y: event.clientY,
      rect: element.getBoundingClientRect(),
      pointer: event.pointerId,
    };
    let active = false;
    let target: Target | null = null;
    function dispose() {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", cancel);
      window.removeEventListener("blur", cancel);
      window.removeEventListener("keydown", key, true);
      cleanup.current = null;
      if (active)
        suppress.current = { id: bar.item.id, until: performance.now() + 400 };
      setDrag(null);
    }
    function cancel() {
      dispose();
    }
    function key(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        cancel();
      }
    }
    function move(event: PointerEvent) {
      if (event.pointerId !== origin.pointer) return;
      const dx = event.clientX - origin.x,
        dy = event.clientY - origin.y;
      if (!active) {
        if (Math.abs(dx) >= 8 && Math.abs(dx) > Math.abs(dy)) {
          cancel();
          return;
        }
        if (Math.abs(dy) < 8) return;
        active = true;
      }
      event.preventDefault();
      const bounds = scope!.getBoundingClientRect();
      target = null;
      if (event.clientY >= bounds.top && event.clientY <= bounds.bottom) {
        for (const row of scope!.querySelectorAll<HTMLElement>(
          '[data-calendar-task="true"]',
        )) {
          const other = tasks.find((task) => task.id === row.dataset.itemId);
          if (!other || other.id === bar.item.id) continue;
          const first = [bar.start, other.start!, row.dataset.calendarStart!]
            .sort()
            .at(-1)!;
          const last = [
            bar.end,
            other.end!,
            row.dataset.calendarEnd!,
          ].sort()[0];
          if (first > last) continue;
          const rect = row.getBoundingClientRect();
          if (event.clientY < rect.top - 2 || event.clientY > rect.bottom + 2)
            continue;
          target = {
            id: other.id,
            segment: row.dataset.calendarStart!,
            date: date >= first && date <= last ? date : first,
            after:
              event.clientY > rect.top + rect.height / 2 ||
              (event.clientY === rect.top + rect.height / 2 && dy > 0),
          };
          break;
        }
      }
      setDrag({
        id: bar.item.id,
        dy: Math.max(
          bounds.top - origin.rect.top,
          Math.min(bounds.bottom - origin.rect.bottom, dy),
        ),
        target,
      });
    }
    function finish(event: PointerEvent) {
      if (event.pointerId !== origin.pointer) return;
      if (active) move(event);
      const destination = target;
      dispose();
      if (!active || !destination) return;
      const before = calendarDay(
        tasks.map((item) => ({ item, start: item.start!, end: item.end! })),
        destination.date,
      ).map((bar) => bar.item);
      const ordered = moveRelative(
        before,
        bar.item.id,
        destination.id,
        destination.after,
      );
      if (ordered.every((item, index) => item.id === before[index].id)) return;
      void execute("/calendar/reorder", "POST", {
        date: destination.date,
        ids: ordered.map((item) => item.id),
        revisions: Object.fromEntries(
          ordered.map((item) => [item.id, item.revision]),
        ),
      }).catch(() => {});
    }
    cleanup.current = cancel;
    window.addEventListener("pointermove", move, { passive: false });
    window.addEventListener("pointerup", finish);
    window.addEventListener("pointercancel", cancel);
    window.addEventListener("blur", cancel);
    window.addEventListener("keydown", key, true);
  }
  function captureClick(event: MouseEvent, id: string) {
    if (
      suppress.current?.id === id &&
      performance.now() < suppress.current.until
    ) {
      event.preventDefault();
      event.stopPropagation();
      suppress.current = null;
    }
  }
  return { drag, start, captureClick };
}
